/**
 * list-filter.js — 列表頁「分類 + 關鍵字」共用篩選控制器
 *
 * articles（專欄）與 products（產品）兩頁的列表篩選共用同一套行為：
 *   - 條件 AND 疊加：分類（chip）與關鍵字（搜尋框）同時滿足才顯示。
 *   - 分類單選：點 chip 切換 is-active / aria-pressed，data-filter="all" 顯示全部。
 *   - 關鍵字即時篩：input 事件即比對（無 submit），清空回到只受分類篩選。
 *   - 無結果空狀態切換：可見數歸零時顯示 [data-list-empty]。
 *
 * 兩頁 DOM 模型不同（articles 扁平 grid / products 卡片落在分類 section 內），
 * 故核心「過濾 predicate + chip 委派 + 搜尋 debounce + 空狀態」抽在此，
 * 各頁差異（如何取分類 / 取搜尋文字 / 顯隱 / 篩後收尾）由 config 注入，不硬湊。
 *
 * 觸發元件可換（chipSelector / filterAttr）：前台列表用 .filter-chip + data-filter（預設），
 * 後台 doctors 工具列用 .tabs__item + data-filter-value（admin-table.js 同源慣例）。
 * 兩者「單選切換 + 委派過濾」行為一致，差異只在選擇器與屬性名，故參數化而非複製整套引擎
 * （search.js 早期因選擇器不符被迫自寫 tab 委派，此次以 config 收斂，避免第二份重複）。
 *
 * 慣例對齊既有 pages/articles.js：root 守門早退、單一委派 listener、pagehide 釋放。
 *
 * 純前端篩選：所有過濾在 client 端對既有 DOM 做顯隱，不打任何 API（無分頁 / 無後端查詢）。
 * 卡片資料由各頁 HTML 在 build 時就全部 render 出來，本引擎只切 .hidden，故無 backend hook。
 */

/* ===== 純函式 predicate（無副作用，可單獨測試）===== */

/** 分類命中：filter==='all' 一律通過，否則需與卡片分類字串相等。 */
function matchCategory(cardCategory, filter) {
  return filter === 'all' || cardCategory === filter;
}

/** 關鍵字命中：空字串一律通過，否則卡片搜尋文字需含關鍵字（皆已轉小寫）。 */
function matchKeyword(searchText, keyword) {
  return keyword === '' || searchText.includes(keyword);
}

/**
 * 建立列表篩選控制器。
 *
 * @param {Object} config
 * @param {HTMLElement}   config.chipsRoot     觸發元件容器（事件委派掛這）
 * @param {HTMLElement[]} config.chips          所有觸發元件（chip / tab）
 * @param {string} [config.chipSelector]        觸發元件選擇器（預設 '.filter-chip'，doctors 傳 '.tabs__item'）
 * @param {string} [config.filterAttr]          分類值屬性名（預設 'data-filter'，doctors 傳 'data-filter-value'）
 * @param {HTMLInputElement|null} config.searchInput 關鍵字輸入框（可無）
 * @param {HTMLElement[]} config.cards          所有卡片元素
 * @param {HTMLElement|null} config.emptyEl     無結果空狀態元素（可無）
 * @param {(card:HTMLElement)=>string} config.getCategory   取卡片分類字串
 * @param {(card:HTMLElement)=>string} config.getSearchText 取卡片可搜尋文字（建議預先小寫）
 * @param {(card:HTMLElement, visible:boolean)=>void} [config.setVisible] 顯隱卡片（預設 el.hidden）
 * @param {(visibleCount:number)=>void} [config.onAfterFilter] 篩後收尾（如收合空 section）
 * @returns {{destroy:()=>void}|null}
 */

/* ===== 工廠：建立控制器（封裝狀態 + 事件 + 生命週期）===== */
export function createListFilter(config) {
  const {
    chipsRoot,
    chips,
    chipSelector = '.filter-chip',
    filterAttr = 'data-filter',
    searchInput,
    cards,
    emptyEl,
    getCategory,
    getSearchText,
    setVisible = (card, visible) => { card.hidden = !visible; },
    onAfterFilter,
  } = config;

  if (!chipsRoot || !chips || !chips.length || !cards || !cards.length) return null;

  // 目前篩選狀態（分類預設 all；關鍵字預設空）
  let activeFilter = 'all';
  let keyword = '';

  // 預先快取每張卡的分類 / 搜尋文字，避免每次 keystroke 重讀 DOM
  const meta = cards.map((card) => ({
    card,
    category: getCategory(card),
    search: getSearchText(card),
  }));

  /** 依當前 activeFilter + keyword 重算全部卡片顯隱，回傳可見數並切換空狀態。 */
  function apply() {
    let visibleCount = 0;
    meta.forEach(({ card, category, search }) => {
      const visible = matchCategory(category, activeFilter) && matchKeyword(search, keyword);
      setVisible(card, visible);
      if (visible) visibleCount += 1;
    });

    if (emptyEl) emptyEl.hidden = visibleCount !== 0;
    if (onAfterFilter) onAfterFilter(visibleCount);
    return visibleCount;
  }

  /** chip 單選態：命中者 is-active + aria-pressed=true，其餘還原。 */
  function setActiveChip(activeChip) {
    chips.forEach((chip) => {
      const isActive = chip === activeChip;
      chip.classList.toggle('is-active', isActive);
      chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  /* ===== 事件處理（委派 + 即時搜尋）===== */

  /** chip 點擊（容器委派）：更新 activeFilter、切單選態、重算顯隱。 */
  function onChipClick(event) {
    const chip = event.target.closest(chipSelector);
    // 委派守門：點到非觸發元件、或不在本容器內，忽略
    if (!chip || !chipsRoot.contains(chip)) return;

    activeFilter = chip.getAttribute(filterAttr) || 'all';
    setActiveChip(chip);
    apply();
  }

  /** 搜尋框輸入：取值 trim + 轉小寫存入 keyword，即時重算（無 debounce，純比對成本低）。 */
  function onSearchInput() {
    keyword = searchInput.value.trim().toLowerCase();
    apply();
  }

  chipsRoot.addEventListener('click', onChipClick);
  if (searchInput) searchInput.addEventListener('input', onSearchInput);

  /* ===== 生命週期 ===== */

  /** 解除所有 listener。對齊 articles.js 慣例由 pagehide 觸發，避免 bfcache 還原時重複綁定。 */
  function destroy() {
    chipsRoot.removeEventListener('click', onChipClick);
    if (searchInput) searchInput.removeEventListener('input', onSearchInput);
  }
  // pagehide（非 unload）：相容 bfcache；once 確保只跑一次
  window.addEventListener('pagehide', destroy, { once: true });

  return { destroy };
}
